/**
 * 饮食 AI 客户端（通过 Cloudflare Pages 函数 /api/estimate 或 Supabase Edge Function 服务端代理）。
 *
 * 职责：
 *   - 优先通过同源边缘服务 /api/estimate（同源无 CORS 拦截，服务端安全持有 API key）；
 *   - 若配置了 Supabase 登录 → POST ai-estimate（自带 JWT，服务端持 key 代理）；
 *   - 未配置且未登录 → 如实返回 not_configured 降级为本地快速录入。
 *   - 严格遵守铁律 2：客户端包内绝不出现任何服务端/AI 密钥。
 */

import type { NutritionEstimate } from "@jilian/shared";
import type { CorrectionPref } from "@jilian/ai";
import { AUTH_PROVIDER } from "./syncClient";

export interface EstimateRequest {
  mealId: string; // 本地生成的餐食 UUID（服务端幂等：重复请求不重复计费）
  rawText: string;
  mealType: "breakfast" | "lunch" | "dinner" | "snack";
  clientCreatedAt: string;
  prefs?: CorrectionPref[];
  localDate?: string;
}

export type EstimateOutcome =
  | { ok: true; estimate: NutritionEstimate; provider: string; model: string; cached: boolean; budgetRemainingCny?: number; remainingParses?: number }
  | { ok: false; status: "not_configured" | "unauthorized" | "budget_exceeded" | "count_exceeded" | "schema_invalid" | "network" | "server_error"; message: string };

interface ServerResponse {
  status?: string;
  estimate?: NutritionEstimate;
  provider?: string;
  model?: string;
  cached?: boolean;
  reason?: string;
  budgetRemainingCny?: number;
  remainingParses?: number;
}

/**
 * 尝试同源边缘代理 /api/estimate（Cloudflare Pages 函数，存在时可用）。
 *
 * 返回 null 表示**该路径不可用**（未部署 / 网络不可达 / 404），调用方应继续走
 * Supabase Edge Function；返回具体结果则表示已拿到确定答复（含各类降级）。
 *
 * 两个关键点：
 *   - 必须带 Authorization：代理侧据此鉴权，否则任何人都能消耗 AI 额度；
 *   - 必须带 mealId：服务端按 mealId 做幂等，漏传会导致同一餐重复计费
 *     （§32.3 #5 一餐不会被重复计入）。
 */
async function trySameOriginEstimate(
  req: EstimateRequest,
  accessToken: string,
): Promise<EstimateOutcome | null> {
  let res: Response;
  try {
    res = await fetch("/api/estimate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        mealId: req.mealId,
        rawText: req.rawText,
        mealType: req.mealType,
        clientCreatedAt: req.clientCreatedAt,
        prefs: req.prefs ?? [],
        ...(req.localDate ? { localDate: req.localDate } : {}),
      }),
    });
  } catch {
    return null; // 未部署或网络不可达 → 交回 Supabase 路径
  }
  // 非 2xx 一律视为「该路径不可用」，回退 Supabase 直连。
  // 不这样做的话，代理侧少配一个环境变量（500）或鉴权异常（401）
  // 就会让估算整体失败——而它本该只是个可选优化。
  if (!res.ok) return null;

  const data = (await res.json().catch(() => ({}))) as ServerResponse;
  return toOutcomeOrNull(data);
}

/**
 * 把服务端响应（含各类降级）映射为客户端结果。
 * 无法识别时返回 null，表示「交回 Supabase 路径」而非报错。
 */
function toOutcomeOrNull(data: ServerResponse): EstimateOutcome | null {
  if (data.status === "estimated" && data.estimate) {
    const ok: EstimateOutcome & { ok: true } = {
      ok: true,
      estimate: data.estimate,
      provider: data.provider ?? "?",
      model: data.model ?? "?",
      cached: data.cached ?? false,
    };
    if (data.budgetRemainingCny != null) ok.budgetRemainingCny = data.budgetRemainingCny;
    if (data.remainingParses != null) ok.remainingParses = data.remainingParses;
    return ok;
  }
  if (
    data.status === "budget_exceeded" ||
    data.status === "count_exceeded" ||
    data.status === "schema_invalid" ||
    data.status === "not_configured"
  ) {
    return { ok: false, status: data.status, message: data.reason ?? "估算不可用" };
  }
  return null;
}

export async function requestMealEstimate(req: EstimateRequest): Promise<EstimateOutcome> {
  const provider = AUTH_PROVIDER;
  if (provider.kind === "local") {
    return { ok: false, status: "not_configured", message: "AI 服务未配置：本次记录保留原文并标记「待估算」，使用下方本地快速录入（客户端不伪造估算）。" };
  }
  // 先取会话：AI 估算必须登录，且同源代理需要 access token 才能鉴权
  const session = await provider.getSession();
  if (!session) {
    return { ok: false, status: "unauthorized", message: "尚未登录：AI 估算需要登录账户。" };
  }

  // 1. 同源边缘代理（Cloudflare Pages 函数，若已部署）
  const viaSameOrigin = await trySameOriginEstimate(req, session.access_token);
  if (viaSameOrigin) return viaSameOrigin;

  // 2. 否则走服务端 Edge Function 代理
  const client = (provider as unknown as { getClient: () => import("@supabase/supabase-js").SupabaseClient }).getClient();
  try {
    const { data, error } = await client.functions.invoke<ServerResponse>("ai-estimate", {
      body: {
        mealId: req.mealId,
        rawText: req.rawText,
        mealType: req.mealType,
        clientCreatedAt: req.clientCreatedAt,
        prefs: req.prefs ?? [],
        localDate: req.localDate,
      },
    });
    if (error) {
      const msg = extractErrorText(error);
      return { ok: false, status: "server_error", message: msg };
    }
    // 这是权威路径，没有下一站可退：无法识别就如实报错
    return toOutcomeOrNull(data ?? {}) ?? {
      ok: false,
      status: "server_error",
      message: `未知响应：${JSON.stringify(data).slice(0, 200)}`,
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : "网络不可用";
    return { ok: false, status: "network", message };
  }
}

function extractErrorText(error: unknown): string {
  const e = error as { message?: string; context?: { message?: string } };
  const raw = e.context?.message ?? e.message ?? "请求失败";
  // 服务端 jsonError 的 body 为 { error: "..." }，解析出可读部分
  try {
    const parsed = JSON.parse(raw.includes("{") ? raw.slice(raw.indexOf("{")) : "{}") as { error?: string };
    if (parsed.error) return parsed.error;
  } catch {
    /* 保持原样 */
  }
  return raw.slice(0, 200);
}